/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState } from 'react';
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Spinner } from 'reactstrap';
import { getWithExpiry, setWithExpiry } from '../AuthService'
import * as default_data from '../config.js'
import { Alert, Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MdMail } from 'react-icons/md';
import { FaLongArrowAltLeft } from "react-icons/fa";
import { getBannerColor } from '../Utils/classification_helper.js';
import { getLoginLogo } from '../Utils/file_functions.js';

function LossPass1Form() {

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    var cookie = getWithExpiry('lost-pass-sent-v2')
    if(cookie == 'true') {
      setErrorMessage('Email already sent, please contact customer support');
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/lost-pass', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while logging in.');
      })
      .then(data => {
        if(data.status != 200) {
            throw new Error(data.response);
        }
        data = data.response;
        setWithExpiry('lost-pass-sent-v2', true, 3600000)
        setIsLoading(false);
        setErrorMessage('');
        setSuccessMessage('Email sent to your email. Please check it out to login')        
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
        setErrorMessage(error.message);
        setSuccessMessage('')
      });
  };

  const subject = `Reset password - ${window.location.href}`;
  const mailtoLink = `mailto:${default_data.default_support_email}?subject=${encodeURIComponent(subject)}`;  

  return (
    <div>
      { 
        <div className={`header-message ${getBannerColor(false)}`}>
          { default_data.default_banner_override != null && default_data.default_banner_override != '' ?
              default_data.default_banner_override : 'UNCLASSIFIED' 
          }
        </div>            
      }
      <div style={{  backgroundColor: '#121212', height: 'calc(100vh - 16px)'}}>
        <Stack sx={{maxHeight: 'calc(100vh - 16px)'}} direction={"row"} spacing={0}>
          <Box sx={{width: isTablet ? "100vw" : "50vw" , overflowY: 'auto'}} >
            <Container>
              <Box style={{display: 'flex', justifyContent: isTablet ? 'center' : 'start', marginBottom: '8px', marginTop: '32px'}}>
                <a href="/">
                  <img src="/logo.png" style={{width: isTablet ? '90px' : '120px'}} alt="Ask Sage" className="logo" />
                </a>
              </Box>
              <Row className="justify-content-center">
                <Col xs="12" md="8" lg="6">
                  <h2 style={{ color: 'white', fontWeight: '500' }}>Forgot Password?</h2>
                  <Typography variant="body2" style={{ color: 'lightgray', marginBottom: '24px' }}>
                    Please enter your email below to receive password reset instructions
                  </Typography>
                  {default_data.default_email_disabled === true ? 
                    <div><a class="white" href={mailtoLink}>Sorry, email is disabled on this tenant so please click here to email us to reset your password on this tenant.</a><br /><br /></div> :
                    <Form onSubmit={handleSubmit} className='login-form'>
                      <FormGroup>
                          <Label for="email">Email</Label>
                          <div className='input-wrapper'>
                            <Input required="required" type="email" name="email" id="email" value={email} onChange={event => setEmail(event.target.value)} />
                            <MdMail className="input-icon" />
                          </div>
                      </FormGroup>
                      {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
                      {successMessage && <Alert severity='success'>{successMessage}</Alert>}
                      <Button style={{marginTop: '16px'}} className='login-form-button' type="submit" disabled={isLoading}>
                          {isLoading ? <Spinner size="sm" /> : 'Request password reset'}
                      </Button>
                    </Form>        
                  }
                  <Button className='login-form-button-secondary' style={{marginTop: '8px', marginBottom: '24px'}} block onClick={() => window.location.href = '/register'}>
                    Don't have an account?
                  </Button>
                  <Box style={{display: 'flex', justifyContent: 'center', marginBottom: '20px'}}>
                  <a href="/login" style={{color: 'white'}}>
                    <FaLongArrowAltLeft style={{marginRight: '8px'}}/>
                    Back to login
                  </a>
                  </Box>
                </Col>
              </Row>
              <Row className="justify-content-center reg-page-mode-footer">
                <Col xs="12" md="12" lg="12">
                  Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
                </Col>
              </Row>
            </Container>  
          </Box>
          {!isTablet && (
            <div
              className="space-image"
              style={{
                backgroundImage: "url('/space-background.png')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                width: '50vw',
              }}
            >
              <img src={getLoginLogo()} alt="Space Girl" />
            </div>
          )}
        </Stack>
      </div>
    </div>
  );
}

export default LossPass1Form;